import isSdkMode from 'util/is-sdk-mode';

import { lazy, useContext, useEffect, useRef, useState } from 'react';

import config from 'config/config';
import WidgetType from 'const/widget-type-constant';
import { ChatWindowOpenStyle, POPUP_WINDOW } from 'context/app-window-style';
import { useCall } from 'context/call-context';
import { usePermission } from 'context/permission-context';
import { useStoreDetails } from 'context/store-context';
import isBaseNameAvailable from 'helper/is-base-name-available';
import { Action, Index } from 'hooks/models';
import useActivityLogger from 'hooks/useActivityLogger';
import useScreenSize from 'hooks/useScreenSize';

import { ChatContext } from '../chat-window/chat-window';

import ChatBubbleStrip from './chat-bubble-strip';
import { usePopupChat } from './usePopupChat';

const ChatFrame = lazy(() => import('components/chat-frame/chat-frame'));
const ChatBubble = lazy(() => import('./chat-bubble'));
const ChatBubbleSideBar = lazy(() => import('./chat-bubble-side-bar'));

interface PopupChatProps {
  baseUrl: string;
}

interface IDataDispatchProduct {
  brand: string;
  category: string;
  price: string | number;
  productId: string;
  productName: string;
  sku: string;
}

const PopupChat = ({ baseUrl }: PopupChatProps) => {
  const firstRender = useRef<boolean>();
  const eventLogger = useRef<boolean>(true);
  const [showWidget, setShowWidget] = useState<boolean>(true);
  const [appointment, setAppointment] = useState<boolean>(false);
  const { isMobile } = useScreenSize();
  const { directCallEnabled, team, chatWindowOpenStyle, product, isActiveLastSession, currentUrl } = useCall();
  const { showSurveyPrompt, session } = useContext(ChatContext)!;
  const { splashScreen, widgetOption } = useStoreDetails();
  const { streamRef } = usePermission();
  const {
    togglePopup,
    isPopupOpen,
    chatEnabled,
    startPolling,
    stopPolling,
    availableAgent,
    pollForAgents,
    noAvailableAgents,
    isAgentsOnline
  } = usePopupChat();

  const { log } = useActivityLogger();

  const URLParams = new URLSearchParams(window.location.search);
  const isChatFrameOpen =
    (isPopupOpen || isActiveLastSession) &&
    (chatWindowOpenStyle === ChatWindowOpenStyle.MODEL || window.name === POPUP_WINDOW);

  useEffect(() => {
    if (isSdkMode()) {
      toggleOfflinePopup();
    }
  }, []);

  useEffect(() => {
    if (!isChatFrameOpen && streamRef.current?.active) {
      streamRef.current?.getTracks().forEach((item) => item.stop());
    }
  }, [streamRef, isChatFrameOpen]);

  useEffect(() => {
    firstRender.current = true;
  }, [currentUrl]);

  const toggleOfflinePopup = async () => {
    const agentAvailability = await pollForAgents(baseUrl, team);

    if (agentAvailability === undefined && splashScreen?.enabled) {
      togglePopup();
    }
  };

  // TODO: This method is client specific, it can be removed, once the client no longer available - client name: JL
  const handleDataStateChange = () => {
    const dispatchDataStateChange = (
      dataDispatchProducts = {
        brand: '',
        category: '',
        price: '',
        productId: '',
        productName: '',
        sku: ''
      } as IDataDispatchProduct
    ) => {
      document.body.dispatchEvent(
        new CustomEvent('dataStateChange', {
          detail: {
            action: 'videoChat',
            products: [dataDispatchProducts],
            type: 'initiated'
          }
        })
      );
    };

    try {
      const productPage = jlData?.page?.productPages || {};
      const products = {
        brand: productPage.brand || '',
        category: productPage.category || '',
        price: productPage.price || '',
        productId: productPage.productId || '',
        productName: productPage.productName || '',
        sku: productPage.displayedSku || ''
      };
      dispatchDataStateChange(products);
    } catch (e) {
      dispatchDataStateChange();
      console.log('jlData is not defined');
    } finally {
      eventLogger.current = false;
      console.log('dataStateChange logged');
    }
  };

  const onBubbleButtonClick = () => {
    void log({
      action: Action.CLICK_LIVE_CHAT_BUTTON,
      description: 'click live chat button by customer',
      index: Index.ACTIVITY,
      label: 'CUSTOMER_WIDGET'
    });

    togglePopup();
    // TODO: This method is client specific, it can be removed, once the client no longer available - client name: JL
    if (eventLogger.current) {
      handleDataStateChange();
    }
  };

  const onOpenProductExpertView = () => {
    stopPolling();
  };

  const onAppointmentClick = () => {
    stopPolling(() => {
      togglePopup();
      setAppointment(true);
    });

    void log({
      action: Action.ON_SECONDARY_BUTTON_CLICKED,
      description: `customer clicked Appointment`,
      index: Index.ACTIVITY,
      label: 'CUSTOMER_WIDGET'
    });
  };

  useEffect(() => {
    if (!appointment) {
      if (!isSdkMode() || directCallEnabled) {
        void startPollingForAgents();
      }

      if (isSdkMode() && URLParams.get('conferwith-show-splash') === 'true') {
        void startPollingForAgents();
      }
    }
    return () => {
      stopPolling();
    };
  }, [appointment]);

  const startPollingForAgents = async () => {
    if (directCallEnabled) {
      await pollForAgents(baseUrl, team);
      togglePopup();
    } else {
      if (!appointment) {
        if (firstRender.current) {
          firstRender.current = false;
          const selectedAgent = await pollForAgents(baseUrl, team);
          void log({
            action: Action.SHOWING_WIDGET_TO_CUSTOMER,
            description: `showing widget to customer - agent  ${selectedAgent ? 'online' : 'offline'}`,
            index: Index.ACTIVITY,
            label: 'CUSTOMER_WIDGET',
            products: [
              {
                id: product?.id,
                name: product?.title
              }
            ]
          });
          setTimeout(() => {
            startPolling(baseUrl, team);
          }, 3000);
        } else {
          startPolling(baseUrl);
        }
      }
    }
  };

  useEffect(() => {
    const isBlackListedUrl = config.blackListedURLs.includes(currentUrl);
    setShowWidget(!isBlackListedUrl);
  }, [currentUrl]);

  const renderChatBubble = (): JSX.Element => {
    const commonProps = {
      chatEnabled,
      isAgentsOnline,
      onAppointmentClick,
      onBubbleButtonClick
    };
    // TODO: this can be removed once all the clients migrated
    if (!widgetOption?.widgetType) {
      return widgetOption?.type === WidgetType.SIDE_BAR ? (
        <ChatBubbleSideBar {...commonProps} />
      ) : (
        <ChatBubble {...commonProps} />
      );
    }

    if (widgetOption.widgetType && isMobile) {
      switch (widgetOption.widgetType.mobile) {
        case WidgetType.SIDE_BAR:
          return <ChatBubbleSideBar {...commonProps} />;
        case WidgetType.STRIP:
          return <ChatBubbleStrip {...commonProps} />;
        default:
          return <ChatBubble {...commonProps} />;
      }
    }

    if (widgetOption.widgetType?.desktop === WidgetType.SIDE_BAR) {
      return <ChatBubbleSideBar {...commonProps} />;
    }

    return <ChatBubble {...commonProps} />;
  };

  // TODO: This needs to removed once the proper solution found
  useEffect(() => {
    const conferDive = window.document.getElementById('conferwith');
    if (!conferDive) return;

    if (isBaseNameAvailable() && !isChatFrameOpen && !session && !showSurveyPrompt) {
      conferDive.style.zIndex = '14';
    } else {
      conferDive.style.zIndex = '99999999999';
    }
  }, [isChatFrameOpen, session, showSurveyPrompt]);

  return (
    <>
      {isChatFrameOpen && (
        <ChatFrame
          appointment={appointment}
          availableAgent={availableAgent}
          chatEnabled={chatEnabled}
          noAvailableAgents={noAvailableAgents}
          isAgentsOnline={isAgentsOnline}
          onClickClose={togglePopup}
          onOpenProductExpertView={onOpenProductExpertView}
          onAppointmentClick={onAppointmentClick}
        />
      )}
      {showWidget &&
        !isActiveLastSession &&
        widgetOption?.enabled !== false &&
        !isPopupOpen &&
        !appointment &&
        renderChatBubble()}
    </>
  );
};

export default PopupChat;
